import React, { useState, useEffect, useContext, lazy, Suspense } from 'react';
import { Calculator, Receipt, TrendingUp, Landmark } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { PageHeader } from '../../../components/ui/page-header';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import { ArcaContext } from '../../../context/ArcaContext.context';
import getCurrentUser from '../../../api/users/getCurrentUser';
import { reasonStopsContributions } from '../../../utils/rescueDocsCatalog';
import useWithdrawalRequests from '../../../hooks/useWithdrawalRequests';
import useRescuesEnabled from '../../../hooks/useRescuesEnabled';

// Lazy load V2 simulators
const PensionSimulatorV2 = lazy(
	() => import('../../../components/SimulatorsV2/PensionSimulatorV2')
);
const TaxSavingsSimulatorV2 = lazy(
	() => import('../../../components/SimulatorsV2/TaxSavingsSimulatorV2')
);
const InflationSimulatorV2 = lazy(
	() => import('../../../components/SimulatorsV2/InflationSimulatorV2')
);
// UX SCAFFOLD (tarea Notion "Definimos MVP rescates", pieza 4) — sin cálculo real,
// ver comentario en RescueTaxSimulatorV2.tsx. Hoy esta pestaña es visible para
// cualquier perfil que vea Simuladores; falta decidir si se abre también a
// individuales (hoy `useAccountNavigationData` solo la muestra a autónomos).
const RescueTaxSimulatorV2 = lazy(
	() => import('../../../components/SimulatorsV2/RescueTaxSimulatorV2')
);

interface SimulatorsV2Props {
	/** Su edad, solo si la sabemos (fecha de nacimiento). */
	currentAge?: number;
	/** Slug del catálogo de su plan elegido, para la rentabilidad del planificador (chirigota#598). */
	planCatalogSlug?: string | null;
	/** `GET /users/plans` aún no ha llegado (el planificador espera para no usar otro plan). */
	plansLoading?: boolean;
}

const Loading = () => (
	<div className="flex items-center justify-center py-20">
		<div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
	</div>
);

const SimulatorsV2: React.FC<SimulatorsV2Props> = ({
	currentAge,
	planCatalogSlug,
	plansLoading
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { token, companyId, userId } = useContext(ArcaContext);

	const [activeTab, setActiveTab] = useState('planificador');
	// Los simuladores de planificación/IRPF/inflación asumen aportación mensual
	// futura, que deja de tener sentido cuando este plan ya no admite
	// aportaciones. Sólo cuenta una solicitud CONFIRMADA por un motivo que corta.
	const { activeStoppingReason } = useWithdrawalRequests(token, 'EMPLOYMENT');
	// Puerta de rescates: cerrada por defecto mientras no sepamos por Caser
	// quién tiene algo rescatable.
	const { enabled: rescuesEnabled } = useRescuesEnabled(token, companyId ?? null, userId);
	const isRetiredBeneficiary = rescuesEnabled && reasonStopsContributions(activeStoppingReason);

	useEffect(() => {
		if (isRetiredBeneficiary) setActiveTab('rescate');
	}, [isRetiredBeneficiary]);

	// Reset scroll on mount
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const triggerClass =
		'flex items-center justify-center gap-2 py-2.5 px-3 text-xs sm:text-sm font-medium rounded-full ' +
		'data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground ' +
		'data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-xs ' +
		'transition-colors';

	return (
		<div className="individual-v2 -mx-4 -mt-4 min-h-screen">
			{/* ⚠️ `px-*`/`pb-*`, NO el atajo `p-*`: el aire de arriba lo pone ahora `PageHeader`
			    (`pt-12 lg:pt-14`) y son elementos distintos, así que un `p-4` aquí se SUMARÍA y
			    subiría la cabecera de 48/56 px a 64/80. Pasó en Proa y se corrigió en la v0.10.0. */}
			<div className="max-w-5xl mx-auto space-y-8 px-4 pb-4 lg:px-6 lg:pb-6">
				{/* Cabecera — la pone `ui/PageHeader`, puerto de Proa v0.10.0. Esta pantalla ya la
				    tenía canónica a mano: era una de las dos que se tomaron como referencia. Ahora
				    la hereda, que es el objetivo — que deje de depender de copiarla bien.
				    `width="full"` porque el ancho lo sigue poniendo el contenedor (`max-w-5xl`). */}
				<PageHeader
					width="full"
					title={translate('simulator.title')}
					subtitle={translate('simulator.subtitle')}
				/>

				{/* Para una beneficiaria/jubilada, Planificador/IRPF/Inflación asumen
				    aportación mensual futura (yearsToRetirement, monthlyContribution)
				    — ya no aplican. Solo tiene sentido Rescate, sin pestañas. */}
				{isRetiredBeneficiary ? (
					<Suspense fallback={<Loading />}>
						<RescueTaxSimulatorV2 />
					</Suspense>
				) : (
					<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
						<TabsList className="grid grid-cols-4 w-full h-auto gap-1 p-1 bg-muted/50 rounded-full">
							<TabsTrigger value="planificador" className={triggerClass}>
								<Calculator className="w-4 h-4" />
								<span className="hidden sm:inline">{translate('simulator.pension.tabTitle')}</span>
								<span className="sm:hidden">{translate('simulator.pension.tabAbbrev')}</span>
							</TabsTrigger>
							<TabsTrigger value="fiscal" className={triggerClass}>
								<Receipt className="w-4 h-4" />
								<span className="hidden sm:inline">{translate('simulator.irpf.tabTitle')}</span>
								<span className="sm:hidden">{translate('simulator.irpf.tabAbbrev')}</span>
							</TabsTrigger>
							<TabsTrigger value="inflacion" className={triggerClass}>
								<TrendingUp className="w-4 h-4" />
								<span className="hidden sm:inline">
									{translate('simulator.inflation.tabTitle')}
								</span>
								<span className="sm:hidden">{translate('simulator.inflation.tabAbbrev')}</span>
							</TabsTrigger>
							<TabsTrigger value="rescate" className={triggerClass}>
								<Landmark className="w-4 h-4" />
								<span className="hidden sm:inline">{translate('rescue.simulatorsTab.full')}</span>
								<span className="sm:hidden">{translate('rescue.simulatorsTab.abbrev')}</span>
							</TabsTrigger>
						</TabsList>

						<div className="mt-6">
							<TabsContent value="planificador">
								<Suspense fallback={<Loading />}>
									<PensionSimulatorV2
										currentAge={currentAge}
										planCatalogSlug={planCatalogSlug}
										plansLoading={plansLoading}
									/>
								</Suspense>
							</TabsContent>

							<TabsContent value="fiscal">
								<Suspense fallback={<Loading />}>
									<TaxSavingsSimulatorV2 />
								</Suspense>
							</TabsContent>

							<TabsContent value="inflacion">
								<Suspense fallback={<Loading />}>
									{/* Solo pinta (no pide rentabilidades): sin su edad, 30 años de referencia. */}
									<InflationSimulatorV2 currentAge={currentAge ?? 30} />
								</Suspense>
							</TabsContent>

							<TabsContent value="rescate">
								<Suspense fallback={<Loading />}>
									<RescueTaxSimulatorV2 />
								</Suspense>
							</TabsContent>
						</div>
					</Tabs>
				)}
			</div>
		</div>
	);
};

export default SimulatorsV2;
